import React, { useState, useContext, useEffect } from 'react';
import { Button } from '@mui/material';
import { Slider } from '@mui/material';
import { MapContext } from '../MapContext';

import ColorMap, { hexToRgba, colorsTurbo } from './ColorMap';


const HexaControls = ({ layer, layerVisibility, styles }) => {
  const { map } = useContext(MapContext);

  // Extract default values
  const defaultRadius = 2.2;
  const defaultColor = "#ccc";

  const [radius, setRadius] = useState(styles?.circleRadius || defaultRadius);
  const [colorField, setColorField] = useState('topK');
  const [colormap, setColormap] = useState({});
  const [colormapRange, setColormapRange] = useState([0, 1]);
  const [isMuted, setIsMuted] = useState(false);

  const fieldNames =
    layer.meta.vector_layers && layer.meta.vector_layers[0].fields
      ? Object.keys(layer.meta.vector_layers[0].fields)
      : [];
  // Ensure 'topK' is at the top of the options list
  const options = [
    'topK',
    ...fieldNames.filter(fieldName => fieldName !== 'topK')];

  const parseColorField = () => {
    if (styles?.colormap) {
      try {
        const colormapJson = JSON.parse(styles.colormap);
        if (colormapJson[0] === "match") {
          return colormapJson[1][1];
        } else if (colormapJson[0] === "interpolate") {
          return colormapJson[2][1];
        } else {
          return null;
        }
      } catch (err) {
        console.error("Error occurred parsing colorfield from bookmark");
        return null;
      }
    } else {
      return null;
    }
  }

  const parseColormapRange = () => {
    if (styles?.colormap) {
      try {
        const colormapJson = JSON.parse(styles.colormap);
        return [colormapJson[3], colormapJson[colormapJson.length - 2]];
      } catch (err) {
        console.error("Error occurred parsing color range from bookmark");
        return null;
      }
    } else {
      return null
    }
  }

  const parseColormap = () => {
    if (styles?.colormap) {
      try {
        const colormapJson = JSON.parse(styles.colormap);
        const newColormap = colormapJson.slice(2, colormapJson.length - 1).reduce((acc, curr, index, array) => {
          if (index % 2 === 0) {
            const rgbaStr = array[index + 1].match(/rgba?\((\d+),\s*(\d+),\s*(\d+),\s*(\d+\.?\d*)\)/);
            acc[curr] = {
              r: parseInt(rgbaStr[1], 10),
              g: parseInt(rgbaStr[2], 10),
              b: parseInt(rgbaStr[3], 10),
              a: parseFloat(rgbaStr[4])
            };
          }
          return acc;
        }, {});
        return newColormap;
      } catch (err) {
        console.error("Error occurred parsing colormap from bookmark");
        return null;
      }
    } else {
      return null;
    }
  }

  useEffect(() => {
    styles?.circleRadius && setRadius(styles.circleRadius);
    if (styles?.colormap) {
      const parsedColorField = parseColorField();
      parsedColorField && setColorField(parsedColorField);
      if (parsedColorField && parsedColorField === "topK") {
        const parsedColormap = parseColormap();
        parsedColormap && setColormap(parsedColormap);
      } else {
        const parsedColormapRange = parseColormapRange();
        parsedColormapRange && setColormapRange(parsedColormapRange);
      }
    }
  }, [styles]);

  useEffect(() => {
    if (map && map.current) {
      map.current.setPaintProperty(
        layer.id,
        'circle-radius',
        [
          "interpolate", ["exponential", 2],
          ["zoom"],
          0, 0,
          21, ['*', 27, ["^", radius, 2]]
        ]
      );
    }
  }, [radius]);

  useEffect(() => {
    if (map && map.current) {
      const initialColormap = constructColormap(layer.meta?.color_map);
      const parsedColorField = parseColorField();
      parsedColorField && setColorField(parsedColorField);
      if (parsedColorField) {
        if (parsedColorField === "topK") {
          const parsedColormap = parseColormap();
          parsedColormap && setColormap(parsedColormap);
        } else {
          const parsedColormapRange = parseColormapRange();
          parsedColormapRange && setColormapRange(parsedColormapRange);
        }
      } else {
        setColormap(initialColormap);
      }
    }
  }, [])

  useEffect(() => {
    if (map && map.current) {
      if (colorField === "topK") {
        updateCircleColorTopK(colormap);
      } else {
        updateCircleColorCont(colormapRange);
      }
    }
  }, [colorField]);

  useEffect(() => {
    updateCircleColorTopK(colormap);
  }, [colormap]);

  useEffect(() => {
    updateCircleColorCont(colormapRange);
  }, [colormapRange]);

  const updateCircleColorTopK = (updatedColormap) => {
    if (map && map.current && (Object.keys(updatedColormap).length !== 0)) {
      map.current.setPaintProperty(
        layer.id, 'circle-color',
        [
          "match", ['get', colorField],
        ].concat(convertColormapToStyleArray(updatedColormap), defaultColor)
      );
    }
  };

  const updateCircleColorCont = (range) => {
    if (map && map.current) {
      map.current.setPaintProperty(
        layer.id, 'circle-color',
        [
          "interpolate", ["linear"], ['get', colorField],
        ].concat(colorsTurbo(range))
      );
    }
  };

  const handleColorChange = (category, newHex) => {
    const rgba = hexToRgba(newHex);
    const updatedColormap = { ...colormap };
    // Update the colormap with the new RGBA object
    updatedColormap[category] = rgba;
    setColormap(updatedColormap);
  };

  const handleMuteToggle = () => {
    const muted = !isMuted; // Toggle the mute state
    const updatedColormap = adjustColormapAlpha(muted);
    setIsMuted(muted);
    setColormap(updatedColormap);
  };

  const adjustColormapAlpha = (isMuted) => {
    const alphaValue = isMuted ? 0 : 1;
    const updatedColormap = { ...colormap };

    for (const key in updatedColormap) {
      updatedColormap[key].a = alphaValue;
    }

    return updatedColormap;
  };

  const handleColormapRangeChange = (newRange) => {
    setColormapRange(newRange);
  }

  const changeRadius = (event, newValue) => {
    setRadius(newValue);
  };

  return (
    <div className="controls-container">
      <div className='layerPanelControl'>
        <span>Select Field: </span>
        <select
          value={colorField}
          onChange={(e) => setColorField(e.target.value)}
          disabled={!layerVisibility}
        >
          {options.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      {colorField == 'topK' ?
        <div>
          <div className="header-container">
            <div>Color Map</div>
            <Button
              onClick={handleMuteToggle}
              variant="outlined"
              className="compactButton"
              disabled={!layerVisibility}
            >
              {isMuted ? "Unmute" : "Mute"}
            </Button>
          </div>
          <ColorMap
            colormap={colormap}
            handleColorChange={handleColorChange}
            isHex={false}
          />
        </div>
        :
        <div className='layerPanelControl'>
          <span>Range </span>
          <Slider
            value={colormapRange}
            onChange={(_, newValue) => handleColormapRangeChange(newValue)}
            step={0.01}
            min={0}
            max={1}
            valueLabelDisplay="auto"
            disabled={!layerVisibility}
          />
        </div>
      }
      <div className='layerPanelControl'>
        <span>Radius</span>
        <Slider
          value={radius}
          step={0.05}
          min={0}
          max={6}
          valueLabelDisplay="auto"
          onChange={changeRadius}
          disabled={!layerVisibility}
        />
      </div>
    </div>
  );
}

const constructColormap = (colorMapString) => {
  const rows = colorMapString.split('\n');
  const newColormap = {};

  for (let i = 1; i < rows.length; i++) { // skip the header
    const [name, _, r, g, b] = rows[i].split('\t');
    if (name && r && g && b) { // check for valid data before processing
      newColormap[name] = {
        r: Math.round(parseFloat(r) * 255),
        g: Math.round(parseFloat(g) * 255),
        b: Math.round(parseFloat(b) * 255),
        a: 1
      };
    }
  }
  return newColormap;
};

const convertColormapToStyleArray = (colormap) => {
  const styleArray = [];
  for (const key in colormap) {
    const color = colormap[key];
    const rgbaString = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
    styleArray.push(Number(key), rgbaString);
  }
  return styleArray;
};

export default HexaControls;
